// ---------------------------------------------------------------------------
// AI helper prompt templates — kept separate from component logic so the
// wording can be iterated on after seeing real creator submissions without
// touching the form/wizard code.
// ---------------------------------------------------------------------------

export const PREVIEW_MAX_LENGTH = 220;

// Shared context line so ChatGPT understands what it's writing for, even
// when a prompt is copied and pasted on its own.
const REALITYCHECK_CONTEXT =
  "You are writing a listing for RealityCheck, a discovery platform for immersive entertainment.";

// Stage 1: marketing description -> RealityCheck Experience Description
export const buildExperienceDescriptionPrompt = (marketingDescription) =>
  `${REALITYCHECK_CONTEXT}

Rewrite the marketing description below into a RealityCheck experience listing. The goal is to help someone decide whether they want to spend their time on this experience.

Focus on:
- What participants actually do
- How they interact
- What they see
- Who the experience is best suited for
- What makes it memorable

Avoid:
- Hype
- Exaggerated marketing language
- Company history
- Ticket information
- Awards
- Calls to action
- Repetitive adjectives

Preserve important factual information. Do not invent details. Write 2–4 short paragraphs using plain language suitable for a discovery platform.

Marketing description:
${marketingDescription || "[PASTE YOUR MARKETING DESCRIPTION HERE]"}`;

// Stage 2: RealityCheck Experience Description -> RealityCheck Preview
export const buildPreviewPrompt = (experienceDescription) =>
  `${REALITYCHECK_CONTEXT}

Write a one- or two-sentence RealityCheck Preview for the experience description below, under approximately ${PREVIEW_MAX_LENGTH} characters.

It should make someone immediately understand:
- What the experience is
- Why it is worth checking out

Avoid hype, vague adjectives, and ad language. Preserve important factual information. Do not invent details.

Example style:
"Step into a location-based VR adventure where teams solve environmental puzzles and explore a cinematic sci-fi world together."

Experience Description:
${experienceDescription || "[PASTE YOUR EXPERIENCE DESCRIPTION HERE]"}`;

// Hero image generation prompt. colorPalette, visualNotes, and
// imageStyleLabel are all optional — when everything is blank, the prompt
// still reads cleanly with "None specified."
export const buildHeroImagePrompt = (title, experienceDescription, colorPalette, visualNotes, imageStyleLabel) => {
  const guidanceParts = [colorPalette, visualNotes].map((s) => (s || "").trim()).filter(Boolean);
  const guidanceText = guidanceParts.length > 0 ? guidanceParts.join(" — ") : "None provided.";
  const styleText = (imageStyleLabel || "").trim() || "None specified.";

  return `${REALITYCHECK_CONTEXT}

Create a high-quality landscape (16:9) promotional image that accurately represents the experience described below, for an immersive entertainment experience called "${
    title || "[TITLE]"
  }."

Focus on the participant's perspective, atmosphere, environment, and interactions. The image should feel authentic and inviting rather than like a movie poster.

If reference images, color palette notes, or a preferred style are provided, use them as visual guidance while still representing the actual experience faithfully.

Experience Description:
${experienceDescription || "[PASTE YOUR EXPERIENCE DESCRIPTION HERE]"}

Preferred color palette / visual notes:
${guidanceText}

Preferred image style:
${styleText}

Do not include text, logos, branding, watermarks, UI elements, borders, or promotional graphics.`;
};
